import { processPortalHandoff, routeMemberFromMain } from "./portalRouting";
import { recordActivity } from "./auditActivity";
import { useState, useEffect, useRef, createContext, useContext } from "react";
import {
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
} from "firebase/auth";
import { getDoc, doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "./firebase";

const AuthContext = createContext(null);

const normaliseEmail = (email) => (email || "").trim().toLowerCase();

function hasEmailLinkParams(href) {
  try {
    const url = new URL(href);
    return url.searchParams.get("mode") === "signIn" || url.searchParams.has("oobCode");
  } catch {
    return false;
  }
}

function clearEmailLinkParams() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("mode");
    url.searchParams.delete("oobCode");
    url.searchParams.delete("apiKey");
    url.searchParams.delete("lang");
    url.searchParams.delete("continueUrl");
    url.searchParams.delete("tenantId");
    const qs = url.searchParams.toString();
    window.history.replaceState(null, "", `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`);
  } catch {
    // ignore
  }
}

export function useAuth() {
  return useContext(AuthContext);
}

function getActionCodeSettings() {
  return {
    url: window.location.origin + "/",
    handleCodeInApp: true,
  };
}

// ─── First-login account linking ─────────────────────────────────────────────
// Ensures the signed-in user's profile document is keyed by their Firebase Auth
// UID (not a random auto-ID), so the Firestore security rules' isAdmin() check —
// which reads users/{uid} — can find it. Runs a trusted server-side function.
// Fails closed: a missing or untrusted staff registration cannot load staff data.
async function ensureLinked() {
  try {
    const linkFn = httpsCallable(functions, "linkUserAccount");
    await linkFn();
  } catch (e) {
    throw e;
  }
}

export function AuthProvider({ children }) {
  const emailLinkPending = useRef(isSignInWithEmailLink(auth, window.location.href));
  const emailLinkStarted = useRef(false);
  const authVersion = useRef(0);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function fetchOwnProfile(uid) {
    const snapshot=await getDoc(doc(db,"users",uid));
    return snapshot.exists()?{...snapshot.data(),id:snapshot.id}:null;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      // The initial signed-out notification can arrive while the link is being
      // exchanged. Keep the loading screen until sign-in and profile loading finish.
      if (!fbUser && emailLinkPending.current) return;
      const version=++authVersion.current;
      setLoading(true);
      setUserProfile(null);
      try {
        const handoff = await processPortalHandoff(fbUser);
        if (handoff === true || version !== authVersion.current) return;
        if (handoff?.user) fbUser = handoff.user;
      } catch(error) { if(version===authVersion.current){setError(error.message);setLoading(false);}return; }
      setFirebaseUser(fbUser);

      if (fbUser) {
        emailLinkPending.current = false;
        try {
          // Link the account to its UID-keyed document before loading the profile.
          // After this runs, the exact profile lookup returns the UID-keyed doc, so
          // profile.id === uid and the security rules' isAdmin() works.
          await ensureLinked();

          const profile = await fetchOwnProfile(fbUser.uid);
          if(version!==authVersion.current)return;

          if (profile && profile.active !== false) {
            setUserProfile(profile);
            setError(null);
          } else if (profile && profile.active === false) {
            setUserProfile(null);
            setError("disabled");
          } else {
            try{if(await routeMemberFromMain())return;}catch{/* Uninvited accounts stay outside the staff system. */}
            setUserProfile(null);
            setError("no-profile");
          }

          if (hasEmailLinkParams(window.location.href)) {
            clearEmailLinkParams();
          }
        } catch (e) {
          if(version!==authVersion.current)return;
          try{if(await routeMemberFromMain())return;}catch{/* Staff access remains independently verified. */}
          console.error("Error fetching user profile:", e);
          setError("fetch-failed");
          setUserProfile(null);
        }
      } else {
        setUserProfile(null);
        if (!emailLinkStarted.current) setError(null);
      }

      if(version===authVersion.current)setLoading(false);
    });

    return ()=>{authVersion.current++;unsubscribe();};
  }, []);

  useEffect(()=>{
    if(!firebaseUser?.uid||!userProfile?.id)return;
    let active=true;
    const unsubscribe=onSnapshot(doc(db,"users",firebaseUser.uid),snapshot=>{
      if(!active)return;
      const profile=snapshot.exists()?{...snapshot.data(),id:snapshot.id}:null;
      if(!profile||profile.active!==true){setUserProfile(null);setError(profile?"disabled":"no-profile");}
      else setUserProfile(profile);
    },()=>{if(active){setUserProfile(null);setError("fetch-failed");}});
    return()=>{active=false;unsubscribe();};
  },[firebaseUser?.uid,userProfile?.id]);

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    if (emailLinkStarted.current) return;
    emailLinkStarted.current = true;

    let email = window.localStorage.getItem("emailForSignIn");

    if (!email) {
      email = window.prompt("Please enter your email to confirm sign-in:");
    }

    if (!email) {
      emailLinkPending.current = false;
      setError("link-failed");
      setLoading(false);
      return;
    }

    const normalisedEmail = normaliseEmail(email);
    setLoading(true);

    signInWithEmailLink(auth, normalisedEmail, window.location.href)
      .then(() => {
        window.localStorage.removeItem("emailForSignIn");
      })
      .catch((err) => {
        emailLinkPending.current = false;
        console.error("Email link sign-in error:", err);
        setError("link-failed");
        setLoading(false);
      });
  }, []);

  const sendLoginEmail = async (email) => {
    try {
      const normalisedEmail = normaliseEmail(email);
      await sendSignInLinkToEmail(auth, normalisedEmail, getActionCodeSettings());
      window.localStorage.setItem("emailForSignIn", normalisedEmail);
      return { success: true };
    } catch (err) {
      console.error("Send sign-in email error:", err);
      return { success: false, error: err.message };
    }
  };

  const logout = async () => {
    await Promise.race([recordActivity("session.end"), new Promise(resolve => setTimeout(resolve,1500))]);
    await signOut(auth);
    setUserProfile(null);
    setFirebaseUser(null);
  };

  const refreshProfile = async () => {
    if (!firebaseUser) return;

    const version=authVersion.current;
    try {
      const profile = await fetchOwnProfile(firebaseUser.uid);
      if(version!==authVersion.current)return;

      if (profile && profile.active !== false) {
        setUserProfile(profile);
      } else { setUserProfile(null); setError(profile ? "disabled" : "no-profile"); }
    } catch (e) {
      console.error("Refresh profile error:", e);
    }
  };

  const value = {
    firebaseUser,
    user: userProfile,
    loading,
    error,
    sendLoginEmail,
    logout,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
